import { useState, useEffect } from 'react';
import { LOGO_CONFIG, getLogoForLocation, getLogo, LOGO_ALIASES } from '../config/logoConfig';

/**
 * Responsive Logo Component with lazy loading and fallback
 * Handles both square (icon) and wide (full brand) logos
 */
export function Logo({ 
  type = 'iconLogo', 
  location,
  size = 'md', 
  className = '',
  onClick,
  showFallback = true,
  responsive = false // Enable responsive width classes
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Resolve legacy aliases
  const resolvedType = LOGO_ALIASES[type] || type;
  
  // Get logo config based on location or type
  const logoConfig = location ? getLogoForLocation(location) : getLogo(resolvedType);
  const sizeConfig = logoConfig.sizes[size] || logoConfig.sizes.md;
  const isWideLogo = logoConfig.aspectRatio === 'wide';
  
  useEffect(() => {
    // Preload image
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.onerror = () => setError(true);
    img.src = logoConfig.src;
  }, [logoConfig.src]);

  // Fallback while loading or on error
  if (!loaded || error) {
    if (!showFallback) return null;
    const fallbackWidth = sizeConfig.width || 200;
    const fallbackHeight = isWideLogo ? 'auto' : (sizeConfig.height || fallbackWidth);
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-primary to-secondary rounded-sm ${className}`}
        style={{ 
          width: fallbackWidth, 
          height: isWideLogo ? fallbackWidth / 3 : fallbackHeight,
          minWidth: fallbackWidth 
        }}
        onClick={onClick}
      >
        <span className="text-white font-heading font-bold text-xl">
          DMV SOUNDSTAGE
        </span>
      </div>
    );
  }

  // Style object for the logo
  const logoStyle = isWideLogo 
    ? {
        // Wide logo: set width, let height auto-scale to maintain aspect ratio
        width: sizeConfig.width,
        maxWidth: '100%',
        height: 'auto'
      }
    : {
        // Square logo: set both width and height
        width: sizeConfig.width || sizeConfig.height,
        height: sizeConfig.height || sizeConfig.width,
        minWidth: sizeConfig.width || sizeConfig.height,
        minHeight: sizeConfig.height || sizeConfig.width
      };

  return (
    <img 
      src={logoConfig.src}
      alt={logoConfig.alt}
      className={`object-contain ${className}`}
      style={logoStyle}
      onClick={onClick}
      loading="lazy"
    />
  );
}

/**
 * Icon Logo - For navbar, favicon, app icon (compact DMV badge)
 */
export function IconLogo({ size = 'md', className = '', onClick }) {
  return <Logo type="iconLogo" size={size} className={className} onClick={onClick} />;
}

/**
 * Full Brand Logo - For landing page, marketing (full DMV SOUNDSTAGE)
 * Uses responsive sizing for different screen sizes
 */
export function FullBrandLogo({ size = 'md', className = '', onClick }) {
  return <Logo type="fullLogo" size={size} className={className} onClick={onClick} />;
}

/**
 * Hero Logo - Extra large responsive logo for hero sections
 * Responsive: Mobile 200px, Tablet 320px, Desktop 400px+
 */
export function HeroLogo({ className = '' }) {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Responsive size based on screen width
  let size = '2xl'; // Desktop: 480px
  if (windowWidth < 640) {
    size = 'md'; // Mobile: 260px
  } else if (windowWidth < 1024) {
    size = 'lg'; // Tablet: 320px
  } else if (windowWidth < 1280) {
    size = 'xl'; // Small desktop: 400px
  }
  
  return (
    <div className={`flex justify-start ${className}`}>
      <Logo type="fullLogo" size={size} className="drop-shadow-2xl" />
    </div>
  );
}

/**
 * Play Logo - Alias for FullLogo (backward compatibility)
 */
export function PlayLogo({ size = 'md', className = '', onClick }) {
  return <Logo type="fullLogo" size={size} className={className} onClick={onClick} />;
}

/**
 * Badge Logo - Alias for IconLogo (backward compatibility)
 */
export function BadgeLogo({ size = 'md', className = '', onClick }) {
  return <Logo type="iconLogo" size={size} className={className} onClick={onClick} />;
}

/**
 * Navbar Logo Component with responsive sizing
 */
export function NavbarLogo({ onClick }) {
  return (
    <div className="flex items-center gap-2" onClick={onClick}>
      <Logo location="navbar" size="lg" className="rounded-sm" />
    </div>
  );
}

/**
 * Landing Hero Logo Component - DEPRECATED, use HeroLogo instead
 */
export function LandingLogo({ size = 'lg', className = '' }) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <Logo location="landingHero" size={size} className="drop-shadow-2xl" />
    </div>
  );
}

/**
 * Media Section Logo (for Soundstage Clips, Music, Videos empty states)
 */
export function MediaLogo({ size = 'lg', className = '' }) {
  return <Logo location="reelsPage" size={size} className={className} />;
}

/**
 * Watermark Logo for videos/content
 */
export function WatermarkLogo({ size = 'sm', className = '', opacity = 0.7 }) {
  return (
    <div className={`absolute bottom-4 right-4 ${className}`} style={{ opacity }}>
      <Logo location="videoWatermark" size={size} />
    </div>
  );
}

/**
 * Auth Page Logo - Large centered logo for login/signup screens
 */
export function AuthLogo({ className = '' }) {
  return (
    <div className={`flex justify-center mb-8 ${className}`}>
      <Logo type="fullLogo" size="lg" className="drop-shadow-xl" />
    </div>
  );
}

export default Logo;
